import {
  QueryClient,
  useInfiniteQuery,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import {
  getTrackingReports,
  TrackingReportPopulated,
} from '@/features/projects/kpi-tracking-entry/api/get-tracking-reports';
import {
  derivativeKpisQueryKey,
  projectKpisQueryKey,
} from '@/features/projects/project-details/hooks/use-project-kpis';

const TRACKING_REPORTS_PAGE_SIZE = 20;

export const getInfiniteTrackingReportsQueryKey = (projectId: number) => [
  'tracking-reports-infinite',
  projectId,
];

export const getTrackingReportsQueryKey = (
  projectId: number,
  limit?: number,
  monthsBack?: number,
) => [
  'tracking-reports',
  projectId,
  ...(limit ? [limit] : []),
  ...(monthsBack ? [monthsBack] : []),
];

/** Invalidates all tracking-report and related KPI queries for a project. */
export function invalidateTrackingReportQueries(
  queryClient: QueryClient,
  projectId: number,
) {
  queryClient.invalidateQueries({
    queryKey: getTrackingReportsQueryKey(projectId),
  });
  queryClient.invalidateQueries({
    queryKey: getInfiniteTrackingReportsQueryKey(projectId),
  });
  queryClient.invalidateQueries({
    queryKey: projectKpisQueryKey(projectId),
  });
  queryClient.invalidateQueries({
    queryKey: derivativeKpisQueryKey(projectId),
  });
}

type Props = UseQueryOptions<
  TrackingReportPopulated[],
  unknown,
  TrackingReportPopulated[]
>;

export const useTrackingReports = ({
  props,
  limit,
  monthsBack,
}: {
  props?: Props;
  limit?: number;
  monthsBack?: number;
}) => {
  const { projectId } = useParams();
  return useQuery({
    ...props,
    queryKey: getTrackingReportsQueryKey(Number(projectId), limit, monthsBack),
    queryFn: async () => {
      if (!projectId) {
        return [];
      }
      return getTrackingReports(Number(projectId), monthsBack, limit);
    },
    enabled: !!projectId,
  });
};

export const useInfiniteTrackingReports = ({
  monthsBack,
}: {
  monthsBack?: number;
} = {}) => {
  const { projectId } = useParams();

  return useInfiniteQuery({
    queryKey: [
      ...getInfiniteTrackingReportsQueryKey(Number(projectId)),
      ...(monthsBack ? [monthsBack] : []),
    ],
    queryFn: async ({ pageParam = 0 }) => {
      if (!projectId) {
        return [];
      }
      return getTrackingReports(
        Number(projectId),
        monthsBack,
        TRACKING_REPORTS_PAGE_SIZE,
        pageParam,
      );
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (lastPage.length < TRACKING_REPORTS_PAGE_SIZE) return undefined;
      return (lastPageParam as number) + TRACKING_REPORTS_PAGE_SIZE;
    },
    enabled: !!projectId,
  });
};
